'use client';

import { useRouter } from 'next/navigation';
import React, { useCallback } from 'react';

import { Button } from './Button';
import { LogoArbitralis } from './LogoArbitralis';

type NotFoundStateProps = {
  title?: string;
  description?: string;
  backHref?: string;
  className?: string;
  leftButtonText?: string;
  rightButtonText?: string;
};

export function NotFoundState({
  title = 'Página não encontrada',
  description = 'A rota que você tentou acessar não existe ou não está disponível.',
  backHref = '/',
  className,
  leftButtonText,
  rightButtonText,
}: NotFoundStateProps) {
  const router = useRouter();

  const handleBack = useCallback(() => {
    if (typeof window === 'undefined') {
      router.push(backHref);
      return;
    }

    if (window.history.length > 1) {
      router.back();
      return;
    }

    router.push(backHref);
  }, [backHref, router]);

  return (
    <div
      className={`bg-default-background fixed inset-0 flex h-screen w-screen items-center justify-center px-6 py-10 ${
        className ?? ''
      }`}
    >
      <div className="relative w-full max-w-[720px] overflow-hidden rounded-[20px] border border-solid border-neutral-200 bg-white p-8 shadow-sm">
        <div className="bg-brand-100 pointer-events-none absolute -top-16 -right-16 h-56 w-56 animate-pulse rounded-full opacity-60 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 animate-pulse rounded-full bg-neutral-100 opacity-80 blur-3xl" />

        <div className="relative flex w-full flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-3">
            <LogoArbitralis className="opacity-90" />
            <div className="relative flex items-center justify-center">
              <div className="bg-brand-50 absolute inset-0 animate-pulse rounded-full opacity-70 blur-xl" />
              <Illustration />
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font text-center">
              {title}
            </span>
            <span className="text-body font-body text-subtext-color text-center">
              {description}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="neutral-secondary" onClick={handleBack}>
              {leftButtonText || 'Voltar'}
            </Button>
            <Button variant="brand-primary" onClick={() => router.push('/')}>
              {rightButtonText || 'Ir para o início'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Illustration() {
  return (
    <svg
      width="220"
      height="140"
      viewBox="0 0 220 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="relative"
    >
      <rect x="28" y="18" width="116" height="92" rx="14" className="fill-neutral-50" />
      <rect x="40" y="34" width="92" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="52" width="72" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="70" width="84" height="10" rx="5" className="fill-neutral-200" />
      <rect x="40" y="88" width="56" height="10" rx="5" className="fill-neutral-200" />

      <circle cx="156" cy="78" r="28" className="fill-brand-100" />
      <circle cx="156" cy="78" r="18" className="fill-white" />
      <circle cx="156" cy="78" r="19" className="fill-brand-200 opacity-50" />

      <rect
        x="172"
        y="86"
        width="44"
        height="10"
        rx="5"
        transform="rotate(35 171 98)"
        className="fill-brand-200 opacity-50"
      />

      <circle cx="10" cy="70" r="6" className="fill-brand-200 animate-bounce opacity-70" />
      <circle cx="199" cy="90" r="5" className="fill-brand-200 animate-bounce opacity-50" />
      <circle cx="110" cy="16" r="6" className="animate-pulse fill-neutral-300 opacity-80" />
      <circle cx="80" cy="16" r="8" className="animate-pulse fill-neutral-300 opacity-80" />
      <circle cx="50" cy="16" r="10" className="animate-pulse fill-neutral-300 opacity-80" />
    </svg>
  );
}
